import { useMemo } from 'react'

import { flatMap } from 'lodash-es'

import { type MapData } from '@/components/organisms/Map/Map.tsx'
import { type TransportRequestMarkerProps } from '@/components/organisms/Map/Markers/TransportRequestMarker'
import { type AppViewData } from '@/components/templates/AppView'
import { type Team } from '@/model/contexts/TeamsProvider.tsx'
import { useRegion } from '@/model/contexts/useRegion.ts'
import { type TransportRequest } from '@/model/contexts/useTransportRequests.ts'
import { teamCarType } from '@/pages/dashboard/data/teamCar'
import { teamStatus } from '@/pages/dashboard/data/teamStatus'

interface Options {
  transportRequests: ReadonlyArray<TransportRequest>
  teams: ReadonlyArray<Team>
}

const transportRequestStatus = (
  request: TransportRequest
): 'pending' | 'assigned' | 'allocated' | 'onboarded' | 'completed' | undefined => {
  switch (request.status) {
    case 'pending':
    case 'assigned':
    case 'allocated':
    case 'completed':
      return request.status
    case 'on_board':
      return 'onboarded'
    default:
      return undefined
  }
}

const mapTeams = (teams: ReadonlyArray<Team>): MapData['teams'] =>
  flatMap(teams, (team) => {
    const status = teamStatus(team.status)
    // remove teams without status
    return status === undefined
      ? []
      : [
          {
            id: team._id,
            carNumber: team.number ?? '?',
            status,
            carType: teamCarType(team.vehicle),
            vip: team.vip,
          },
        ]
  })

const mapPlannedTeam = (
  request: TransportRequest,
  teams: ReadonlyArray<Team>,
  requests: ReadonlyArray<TransportRequest>
): TransportRequestMarkerProps['plannedTeam'] | undefined => {
  if (request.plannedTeamAssignment?.teamId) {
    const team = teams.find(({ _id }) => _id === request.plannedTeamAssignment?.teamId)
    if (team) {
      const currentTeamRequest = requests.find(({ _id }) => _id === team.transportRequest?.id)
      if (currentTeamRequest) {
        return {
          id: request.plannedTeamAssignment.teamId,
          carNumber: team.number,
          carType: teamCarType(team.vehicle),
          vip: team.vip,
          endPosition: currentTeamRequest.destination.geolocation,
        }
      }
    }
  }
  return undefined
}

const mapTransportRequests = (
  transportRequests: ReadonlyArray<TransportRequest>,
  teams: ReadonlyArray<Team>
): MapData['transportRequests'] =>
  transportRequests.map((transportRequest) => ({
    id: transportRequest._id,
    status: transportRequestStatus(transportRequest),
    locationLabel: transportRequest.letters,
    origin: transportRequest.origin.geolocation,
    destination: transportRequest.destination.geolocation,
    team: transportRequest.team && {
      id: transportRequest.team._id,
      carNumber: transportRequest.team.number,
      carType: teamCarType(transportRequest.team.vehicle),
      vip: transportRequest.team.vip,
    },
    plannedTeam: transportRequest.plannedTeamAssignment && mapPlannedTeam(transportRequest, teams, transportRequests),
  }))

export function useMapData({ transportRequests, teams }: Options): Pick<AppViewData, 'map'> {
  const { region } = useRegion()

  return useMemo(
    () => ({
      map: {
        center: region?.mapConfiguration?.center,
        zoom: region?.mapConfiguration?.zoom ?? 11,
        teams: mapTeams(teams),
        transportRequests: mapTransportRequests(transportRequests, teams),
      },
    }),
    [region, transportRequests, teams]
  )
}
